import ButtonDropdown from '@rio-cloud/rio-uikit/components/dropdown/ButtonDropdown';
import ClearableInput from '@rio-cloud/rio-uikit/ClearableInput';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import TableCol from '@rio-cloud/rio-uikit/TableCol';
import type React from 'react';
import { useState } from 'react';
import type { FreeTrialStatus } from '../api/freetrial/types';
import { FreeTrialEligibilityDialog } from './FreeTrialEligibilityDialog';
import { FreeTrialExtensionDialog } from './FreeTrialExtensionDialog';
import { FreeTrialTerminationDialog } from './FreeTrialTerminationDialog';
import { useFreeTrialSearch } from './useFreeTrialSearch';

type LabelStyle = 'success' | 'info' | 'muted';

const statusLabelStyles: Record<FreeTrialStatus, LabelStyle> = {
    ACTIVE: 'success',
    ELIGIBLE: 'info',
    INELIGIBLE: 'muted',
    INACTIVE: 'muted',
};

const formatStatus = (status: FreeTrialStatus) => {
    const style = statusLabelStyles[status];
    return <span className={`label label-${style} label-filled label-condensed`}>{status}</span>;
};

const isAlreadyEligibleForFreeTrial = (status: FreeTrialStatus) => status === 'ACTIVE' || status === 'ELIGIBLE';
const isTerminateFreeTrialDisabled = (status: FreeTrialStatus) => status === 'INACTIVE' || status === 'INELIGIBLE';

export function FreeTrialPage() {
    const [showMakeEligibleDialog, setShowMakeEligibleDialog] = useState(false);
    const [showExtendDialog, setShowExtendDialog] = useState(false);
    const [showTerminateDialog, setShowTerminateDialog] = useState(false);
    const { accountIdInput, setAccountIdInput, search, data: freeTrialState, isFetching } = useFreeTrialSearch();
    const isMakeEligibleActionDisabled = freeTrialState ? isAlreadyEligibleForFreeTrial(freeTrialState.status) : false;
    const isExtendActionDisabled = freeTrialState ? freeTrialState.status !== 'ACTIVE' : true;
    const isTerminateActionDisabled = freeTrialState ? isTerminateFreeTrialDisabled(freeTrialState.status) : true;

    return (
        <div className='module-content'>
            <div className='container-fluid fluid-default'>
                {freeTrialState && (
                    <FreeTrialEligibilityDialog
                        show={showMakeEligibleDialog}
                        freeTrialState={freeTrialState}
                        statusLabel={formatStatus(freeTrialState.status)}
                        onClose={() => setShowMakeEligibleDialog(false)}
                    />
                )}
                {freeTrialState && (
                    <FreeTrialExtensionDialog
                        show={showExtendDialog}
                        freeTrialState={freeTrialState}
                        statusLabel={formatStatus(freeTrialState.status)}
                        onClose={() => setShowExtendDialog(false)}
                    />
                )}
                {freeTrialState && (
                    <FreeTrialTerminationDialog
                        show={showTerminateDialog}
                        freeTrialState={freeTrialState}
                        statusLabel={formatStatus(freeTrialState.status)}
                        onClose={() => setShowTerminateDialog(false)}
                    />
                )}
                <div className='display-flex gap-10 margin-top-20 margin-bottom-20'>
                    <div className='flex-1-1'>
                        <ClearableInput
                            placeholder='Search by accountId ...'
                            value={accountIdInput}
                            onChange={(value: string) => setAccountIdInput(value.trim())}
                            onKeyPress={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter') {
                                    search();
                                }
                            }}
                        />
                    </div>
                    <button
                        type='button'
                        className='btn btn-primary'
                        disabled={!accountIdInput || isFetching}
                        onClick={search}
                    >
                        <span className='rioglyph rioglyph-search margin-right-5' />
                        Find
                    </button>
                </div>

                {isFetching && (
                    <div className='text-center padding-20'>
                        <Spinner />
                    </div>
                )}

                {!isFetching && freeTrialState && (
                    <div className='border border-bottom-only border-color-lighter overflow-x-auto min-height-350'>
                        <table className='table table-head-filled table-hover table-sticky-in-container table-layout-fixed'>
                            <colgroup>
                                <TableCol />
                                <TableCol />
                                <TableCol />
                                <TableCol />
                                <TableCol />
                                <TableCol />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>AccountId</th>
                                    <th>Name</th>
                                    <th>Free Trial Status</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className='text-truncate'>{freeTrialState.accountId}</td>
                                    <td className='text-truncate'>{freeTrialState.accountName}</td>
                                    <td>{formatStatus(freeTrialState.status)}</td>
                                    <td>
                                        <span className='white-space-nowrap'>{freeTrialState.startDate ?? ''}</span>
                                    </td>
                                    <td>
                                        <span className='white-space-nowrap'>{freeTrialState.endDate ?? ''}</span>
                                    </td>
                                    <td>
                                        <ButtonDropdown
                                            title={<span className='rioglyph rioglyph-option-vertical' />}
                                            variant='link'
                                            iconOnly
                                            className='btn-s'
                                            dropdownClassName='overflow-auto'
                                            items={[
                                                {
                                                    value: (
                                                        <div>
                                                            <span className='rioglyph rioglyph-pencil-square margin-right-5 text-size-18' />
                                                            Make eligible for free trial
                                                        </div>
                                                    ),
                                                    disabled: isMakeEligibleActionDisabled,
                                                    onSelect: () => setShowMakeEligibleDialog(true),
                                                },
                                                {
                                                    value: (
                                                        <div>
                                                            <span className='rioglyph rioglyph-calendar margin-right-5 text-size-18' />
                                                            Extend free trial period
                                                        </div>
                                                    ),
                                                    disabled: isExtendActionDisabled,
                                                    onSelect: () => setShowExtendDialog(true),
                                                },
                                                {
                                                    value: (
                                                        <div>
                                                            <span className='rioglyph rioglyph-remove-sign margin-right-5 text-size-18' />
                                                            Terminate free trial
                                                        </div>
                                                    ),
                                                    disabled: isTerminateActionDisabled,
                                                    onSelect: () => setShowTerminateDialog(true),
                                                },
                                            ]}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
